import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Play, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useData, API_BASE } from "../DataContext";
import { LazyImage } from "../components/LazyImage";
import { usePageMeta } from "../hooks/usePageMeta";


export default function Home() {
  usePageMeta(
    "Water Tanks, Pipes & Fittings Manufacturer in India",
    "BAISHALII manufactures food-grade water tanks, uPVC/CPVC/SWR pipes & fittings, stainless steel sinks, cisterns, PTMT taps and garden pipes. Trusted across India since 2006."
  );
  const { HERO_SLIDES, STATS, PRODUCT_CATEGORIES, INDUSTRIES } = useData();
  const [slide, setSlide] = useState(0);
  const [blogPosts, setBlogPosts] = useState([]);
  const [activeCat, setActiveCat] = useState(PRODUCT_CATEGORIES[0].id);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/blog`)
      .then((r) => r.json())
      .then((posts) => setBlogPosts(posts.slice(0, 3)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), HERO_SLIDES[slide].duration || 5500);
    return () => clearTimeout(t);
  }, [slide]);

  const activeProducts = PRODUCT_CATEGORIES.find((c) => c.id === activeCat)?.products || [];

  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 50) return;
    setSlide((s) => (dx < 0 ? (s + 1) % HERO_SLIDES.length : (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length));
  };

  return (
    <div className="pt-[88px] md:pt-[112px]">
      {/* HERO SLIDER */}
      <section className="relative hero-slider-h overflow-hidden bg-slate-900" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="hero-slider">
        {HERO_SLIDES.map((s, i) => (
          <div key={s.id} className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0"}`}>
            {s.video ? (
              <video autoPlay muted loop playsInline preload={i === 0 ? "auto" : "metadata"} className={`absolute inset-0 w-full h-full ${s.hideText && !s.coverMobile ? "object-contain md:object-cover bg-white" : "object-cover"}`} data-testid="hero-video-slide">
                <source src={s.video} type="video/mp4" />
                <source src={s.video.replace(".mp4", ".webm")} type="video/webm" />
              </video>
            ) : s.mobileImage ? (
              <>
                <img src={s.mobileImage} alt={s.title || "BAISHALII"} className="md:hidden absolute inset-0 w-full h-full object-cover" />
                <img src={s.image} alt={s.title || "BAISHALII"} className="hidden md:block absolute inset-0 w-full h-full object-cover [object-position:center_30%]" />
              </>
            ) : (
              <img src={s.image} alt={s.title || "BAISHALII"} className={`absolute inset-0 w-full h-full ${s.hideText ? "object-contain md:object-cover bg-white" : "object-cover"}`} />
            )}
            {!s.hideText && <div className={`absolute inset-0 bg-gradient-to-r from-[#061a3d]/85 via-[#0a2a5e]/60 to-transparent ${s.hideTextMobile ? "hidden md:block" : ""}`} />}
            {!s.hideText && (
            <div className={`relative z-10 max-w-7xl mx-auto px-6 h-full items-center ${s.hideTextMobile ? "hidden md:flex" : "flex"}`}>
              <div className="max-w-2xl text-white">
                <div className="inline-block px-4 py-1.5 mb-5 rounded-full bg-sky-500/20 border border-sky-400/40 text-sky-200 text-xs font-semibold tracking-wider">
                  INDIA'S TRUSTED PIPE BRAND
                </div>
                <h1 className="text-4xl md:text-6xl font-black leading-[1.05] mb-5 tracking-tight">
                  {s.title}
                </h1>
                <p className="text-lg md:text-xl text-slate-200 mb-8 max-w-xl">{s.subtitle}</p>
                <div className="flex flex-wrap gap-3">
                  {s.cta ? (
                    <Button asChild size="lg" className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-7 shadow-lg" data-testid="hero-cta-button">
                      <Link to={s.cta.to}>{s.cta.label} <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                    </Button>
                  ) : (
                  <>
                  <Button asChild size="lg" className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-7 shadow-lg">
                    <Link to="/products">Explore Products <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="rounded-full px-7 bg-white/5 backdrop-blur border-white/30 text-white hover:bg-white hover:text-[#0a2a5e]">
                    <Link to="/about"><Play className="h-4 w-4 mr-1.5" /> Our Story</Link>
                  </Button>
                  </>
                  )}
                </div>
              </div>
            </div>
            )}
          </div>
        ))}

        {/* nav arrows */}
        <button onClick={() => setSlide((slide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)} className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white hover:text-[#0a2a5e] text-white items-center justify-center transition-colors">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button onClick={() => setSlide((slide + 1) % HERO_SLIDES.length)} className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white hover:text-[#0a2a5e] text-white items-center justify-center transition-colors">
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-10 bg-sky-400" : "w-5 bg-white/40"}`} />
          ))}
        </div>
      </section>

      {/* INTRO + STATS */}
      <section className="py-14 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-3">ABOUT BAISHALII</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a5e] leading-tight mb-6">India's Trusted Plumbing Solutions Brand</h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-5">
              Founded in Kolkata in 2006, BAISHALII began its journey with the manufacturing of stainless steel kitchen sinks before expanding into plastic cisterns, seat covers, water storage tanks, household products, PVC garden pipes, and a comprehensive range of uPVC, SWR, and Agricultural Pipes &amp; Fittings.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-5">
              As the <strong>first ISI-certified Stainless Steel Sink manufacturer in West Bengal and Eastern India</strong>, BAISHALII has built a reputation for quality, innovation, and reliability. Today, with 500+ SKUs and a growing network of dealers and plumbers, BAISHALII continues to deliver trusted plumbing solutions that meet the evolving needs of customers across India.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button asChild className="bg-[#0a2a5e] hover:bg-[#061a3d] text-white rounded-full px-6"><Link to="/about">About BAISHALII</Link></Button>
              <Button onClick={() => setVideoOpen(true)} variant="outline" className="rounded-full px-6 border-[#0a2a5e] text-[#0a2a5e] hover:bg-[#0a2a5e] hover:text-white group">
                <span className="h-7 w-7 rounded-full bg-sky-500 group-hover:bg-white text-white group-hover:text-sky-600 flex items-center justify-center mr-2 transition-colors">
                  <Play className="h-3.5 w-3.5 fill-current ml-0.5" />
                </span>
                Watch Corporate Video
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            {STATS.map((s, i) => (
              <div key={i} className="group bg-gradient-to-br from-sky-50 to-white rounded-2xl p-6 border border-sky-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-black text-[#0a2a5e] mb-2 group-hover:text-sky-600 transition-colors">{s.value}</div>
                <div className="text-slate-600 text-xs md:text-sm font-medium leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAGLINE BAND */}
      <section className="py-12 bg-gradient-to-r from-[#061a3d] via-[#0a2a5e] to-sky-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px)", backgroundSize: "30px 30px"}}/>
        <div className="max-w-5xl mx-auto px-6 text-center relative">
          <h2 className="text-3xl md:text-5xl font-black leading-tight">Born in Kolkata — <span className="text-sky-300">Built to Flow Forward.</span></h2>
          <p className="mt-5 text-slate-200 max-w-3xl mx-auto text-lg">BAISHALII is driven by a commitment to quality, innovation, and continuous improvement. We strive to set higher standards and create lasting value for our customers, partners, and the piping industry.</p>
        </div>
      </section>

      {/* PRODUCTS TABS */}
      <section className="py-14 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-3">OUR PORTFOLIO</div>
              <h2 className="text-4xl md:text-5xl font-black text-[#0a2a5e]">We have the best quality products</h2>
            </div>
            <Button asChild className="bg-[#0a2a5e] hover:bg-[#061a3d] text-white rounded-full px-6 self-start md:self-auto"><Link to="/products">All Products <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
          </div>

          {/* tabs */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
            {PRODUCT_CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setActiveCat(c.id)} className={`shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${activeCat === c.id ? "bg-[#0a2a5e] text-white shadow-md" : "bg-white text-slate-700 hover:bg-sky-50 hover:text-sky-700 border border-slate-200"}`}>
                {c.name}
              </button>
            ))}
          </div>

          {/* product grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {activeProducts.map((p, i) => (
              <Link key={i} to={`/products/${p.slug}`} className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-xl hover:border-sky-300 transition-all duration-300">
                <div className="aspect-square overflow-hidden bg-slate-100 flex items-center justify-center p-4">
                  <LazyImage src={p.image} alt={p.name} className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="font-bold text-[#0a2a5e] mb-1 group-hover:text-sky-600 transition-colors">{p.name}</div>
                  <div className="text-xs text-slate-500 leading-snug line-clamp-2">{p.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section className="pt-14 md:pt-16 pb-12 md:pb-14 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-20">
            <div className="lg:sticky lg:top-32 self-start">
              <div className="w-10 h-1 bg-sky-500 mb-6" />
              <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-3">INDUSTRIES WE SERVE</div>
              <h2 className="text-4xl md:text-5xl font-black text-[#0a2a5e] leading-tight">Industries<br />We Serve</h2>
              <p className="text-slate-600 mt-6 text-base md:text-lg leading-relaxed">
                From farmland irrigation to urban plumbing and water storage — BAISHALII products power India's homes, farms and infrastructure.
              </p>
              <Link to="/products" data-testid="industries-see-all-btn" className="inline-flex items-center gap-2 mt-8 bg-gradient-to-r from-sky-500 to-[#0a2a5e] hover:opacity-90 text-white font-semibold rounded-full px-7 py-3 transition-opacity">
                See All Products <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div>
              {INDUSTRIES.map((ind, i) => (
                <Link key={i} to={ind.link} data-testid={`industry-item-${i}`} className="group flex gap-6 md:gap-10 py-8 md:py-10 border-t border-slate-200 first:border-t-0">
                  <div className="text-3xl md:text-4xl font-black text-sky-200 group-hover:text-sky-400 transition-colors leading-none pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-black text-[#0a2a5e] group-hover:text-sky-600 transition-colors">{ind.title}</h3>
                    <p className="text-slate-600 mt-3 leading-relaxed">{ind.desc}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {ind.tags.map((t) => (
                        <span key={t} className="text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-3 py-1.5">{t}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LATEST FROM BLOG */}
      <section className="py-14 md:py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
            <div>
              <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-3">FROM OUR BLOG</div>
              <h2 className="text-4xl md:text-5xl font-black text-[#0a2a5e]">Latest from BAISHALII</h2>
            </div>
            <Link to="/blog" data-testid="home-blog-view-all" className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-600 hover:gap-2.5 transition-all">
              View all articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`home-blog-card-${p.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-shadow flex flex-col">
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  {p.coverImage && <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="text-xs text-sky-600 font-semibold mb-2">
                    {new Date(p.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                  <h3 className="font-bold text-[#0a2a5e] leading-snug group-hover:text-sky-600 transition-colors">{p.title}</h3>
                  <p className="text-sm text-slate-500 mt-2 line-clamp-2 flex-1">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-sky-600 group-hover:gap-2 transition-all">Read more <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#061a3d] via-[#0a2a5e] to-sky-700 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-black mb-5">Ready to build with confidence?</h2>
          <p className="text-slate-200 text-lg mb-8 max-w-2xl mx-auto">Talk to our piping experts and find the right BAISHALII solution for your project.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-white text-[#0a2a5e] hover:bg-sky-50 rounded-full px-7 font-bold"><Link to="/contact">Get in Touch</Link></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7 bg-white/5 backdrop-blur border-white/30 text-white hover:bg-white hover:text-[#0a2a5e]"><Link to="/products">Browse Catalog</Link></Button>
          </div>
        </div>
      </section>
      {/* CORPORATE VIDEO MODAL */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-0">
          <DialogTitle className="sr-only">BAISHALII Corporate Video</DialogTitle>
          <div className="relative aspect-video w-full bg-black">
            {videoOpen && (
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/ldtb1-kR294?autoplay=1&rel=0&modestbranding=1"
                title="BAISHALII Corporate Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
            <a
              href="https://youtu.be/ldtb1-kR294"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 hover:bg-white text-slate-900 text-xs font-semibold shadow-lg transition-colors"
            >
              <Play className="h-3 w-3 fill-current" /> Open on YouTube
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
